/* eslint-disable react/no-children-prop */
import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CookieConsent from 'react-cookie-consent';

import { PlaceContext } from '../../../core/contexts/PlaceContext';
import { initializeGA, initializeGAIfConsented } from '../../../core/functions/analytics';
import { log } from '../../../core/functions/logger';

const parseCookie = (str) => {
  try {
    const parsedCookies = str
      .split(';')
      .map((v) => v.split('='))
      .reduce((acc, v) => {
        try {
          acc[decodeURIComponent(v[0]?.trim())] = decodeURIComponent(v[1]?.trim());
          return acc;
        } catch (err) {
          log('Error parsing cookie accumulator:', err);
          return acc;
        }
      }, {});
    return parsedCookies;
  } catch (err) {
    log('Error parsing cookie:', err);
    return {};
  }
};

const initialCookies = parseCookie(document.cookie);

// Check if user has made a consent decision (either accepted or declined)
const hasConsentDecision = () => {
  return initialCookies['CookieConsent'] !== undefined;
};

const GdprOverlay = () => {
  const placeContext = useContext(PlaceContext);
  const [expandGDPR, setExpandGDPR] = useState(false);

  const { t } = useTranslation();

  const {
    data: { gdpr },
    loading: isLoading,
  } = placeContext;

  // Initialize GA only if user previously accepted (not declined)
  useEffect(() => {
    // Only load GA if consent was given (value is 'true')
    if (initialCookies['CookieConsent'] === 'true') {
      initializeGAIfConsented();
    }
  }, []);

  // Handle accept - load GA and analytics cookies
  const handleAccept = () => {
    initializeGA();
    log('User accepted all cookies');
  };

  // Handle decline - only essential cookies, no GA
  const handleDecline = () => {
    log('User declined analytics cookies - using essential only');
  };

  return (
    <>
      {!isLoading && gdpr && !expandGDPR && !hasConsentDecision() && (
        <div
          className="p-fix p-bottom f-c f-jcsb f-aic mc-lr-2x l12"
          style={{ bottom: 65, height: 64 }}
        >
          <div />
          <button
            className="pe-all bg-gdpr-button p-8 br-4 no-border no-outline no-select cursor-default"
            onClick={() => setExpandGDPR(true)}
          >
            <h3 className="f2b f08 txt-gdpr-button">{t('gdpr.view')}</h3>
          </button>
        </div>
      )}
      {!isLoading && gdpr && expandGDPR && (
        <CookieConsent
          location="top"
          buttonText={t('gdpr.accept_all')}
          declineButtonText={t('gdpr.essential_only')}
          cookieName="CookieConsent"
          sameSite="strict"
          onAccept={handleAccept}
          onDecline={handleDecline}
          enableDeclineButton
          style={{
            margin: '0 15px',
            marginTop: 'calc(95px + 5vw)',
            width: 'calc(100vw - 10vw) !important',
          }}
          overlay
          containerClasses="f-c f-jcc pe-all txt-gdpr-container bg-gdpr-container"
          buttonClasses="gdpr-decision-button"
          declineButtonClasses="gdpr-decline-button"
          buttonWrapperClasses="gdpr-button-wrapper"
          expires={150}
          setDeclineCookie
        >
          <p className="mt-4">{t('gdpr.message.cookie_usage')}</p>
          <p>{t('gdpr.message.cookie_accept')}</p>
          <p className="f06 mt-4">{t('gdpr.message.data_controller')}</p>
          <div className="f06 mt-4 ml-8">
            <p className="f2b">{t('gdpr.message.cookies')}</p>
            <p>• {t('gdpr.message.ga')}</p>
            <p>• {t('gdpr.message.location')}</p>
            <p>• {t('gdpr.message.point')}</p>
            <p>• {t('gdpr.message.mode')}</p>
            <p>• {t('gdpr.message.identifier')}</p>
          </div>
          <p className="mt-4">{t('gdpr.message.warning')}</p>
          <p className="f06">{t('gdpr.message.rights')}</p>
          <p className="f06">{t('gdpr.message.policy_link')}</p>
        </CookieConsent>
      )}
    </>
  );
};

export { GdprOverlay };
